import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import {
  GET_MENU_INDEX,
  GET_PRODUCT_RECONCILIATION,
  GET_PRODUCT_VOID_STATS,
  VOIDS_API_READY,
} from '../../graphql/reports-queries'
import { dateRangeToUtc, formatEuros } from '../../lib/format'
import {
  DateRangeBar,
  EmptyData,
  QueryError,
  TH,
  TableLoading,
  downloadCsvFromRows,
  isValidYmd,
  labelOf,
  parseYmd,
  productVariantKey,
  toYmd,
} from './shared'
import type { OrderSource } from './shared'
import type { DrillTarget } from './ProductDrillPanel'

// ─── Types ────────────────────────────────────────────────────────────────────

interface HourProductRow {
  hour: number
  productId: number
  productName: string
  variantName: string
  qtyScheduled: number
}

interface RevenueRow {
  productId: number
  variantName: string
  qtySold: number
  revenueExVatCents: number | null
}

interface VoidRow {
  productId: number
  variantId: number
  productName: string
  variantName: string
  qtyCancelled: number
  qtyRefunded: number
}

interface MenuVariant {
  id: number
  name: string
  active: boolean
}

interface MenuProduct {
  id: number
  name: string
  kind: string
  active: boolean
  variants: MenuVariant[]
}

type RowStatus = null | 'archived' | 'not in menu'

interface ReconciliationRow {
  key: string
  productId: number
  productName: string
  variantName: string
  label: string
  sold: number
  staff: number
  made: number
  prior: number
  cancelled: number | null
  refunded: number | null
  revenueCents: number | null
  status: RowStatus
}

type PriorMode = 'previous' | 'lastYear'

const PRIOR_MODES: { id: PriorMode; label: string }[] = [
  { id: 'previous', label: 'Previous period' },
  { id: 'lastYear', label: 'Same period last year' },
]

// ─── Prior-period math (pure, local dates only) ───────────────────────────────

const monthRange = (year: number, monthIndex: number) => ({
  start: toYmd(new Date(year, monthIndex, 1)),
  end: toYmd(new Date(year, monthIndex + 1, 0)),
})

const isFullCalendarMonth = (start: string, end: string): boolean => {
  const s = parseYmd(start)
  const e = parseYmd(end)
  return (
    s.getDate() === 1 &&
    s.getFullYear() === e.getFullYear() &&
    s.getMonth() === e.getMonth() &&
    e.getDate() === new Date(e.getFullYear(), e.getMonth() + 1, 0).getDate()
  )
}

/**
 * A whole calendar month compares against a whole calendar month (a 31-day
 * March against a 28-day February is what an operator expects); any other range
 * shifts by its own length in days, or by exactly one year.
 */
const priorRange = (start: string, end: string, mode: PriorMode): { start: string; end: string } => {
  const s = parseYmd(start)
  const e = parseYmd(end)

  if (isFullCalendarMonth(start, end)) {
    return mode === 'previous'
      ? monthRange(s.getFullYear(), s.getMonth() - 1)
      : monthRange(s.getFullYear() - 1, s.getMonth())
  }

  if (mode === 'lastYear') {
    return {
      start: toYmd(new Date(s.getFullYear() - 1, s.getMonth(), s.getDate())),
      end: toYmd(new Date(e.getFullYear() - 1, e.getMonth(), e.getDate())),
    }
  }

  // Round the span: a DST switch inside the range makes it a whole hour short.
  const days = Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1
  const priorEnd = new Date(s)
  priorEnd.setDate(priorEnd.getDate() - 1)
  const priorStart = new Date(priorEnd)
  priorStart.setDate(priorStart.getDate() - (days - 1))
  return { start: toYmd(priorStart), end: toYmd(priorEnd) }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Cents → "1234,56" — the plain decimal-comma number NL Excel parses. */
const csvEuros = (cents: number | null): string | null =>
  cents == null ? null : (cents / 100).toFixed(2).replace('.', ',')

const StatusBadge = ({ status }: { status: RowStatus }) => {
  if (status == null) return null
  const tone =
    status === 'archived' ? 'bg-gray-100 text-gray-500' : 'bg-purple-100 text-purple-700'
  return <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${tone}`}>{status}</span>
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

const ReconciliationTab = ({
  channel,
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  onOpenDrill,
}: {
  channel: OrderSource | null
  startDate: string
  endDate: string
  onStartChange: (v: string) => void
  onEndChange: (v: string) => void
  onOpenDrill: (drill: DrillTarget) => void
}) => {
  const [search, setSearch] = useState('')
  const [priorMode, setPriorMode] = useState<PriorMode>('previous')

  // A date input being cleared or keyboard-edited emits '' — building filters
  // from that would throw mid-render (dateRangeToUtc on an Invalid Date).
  const rangeValid = isValidYmd(startDate) && isValidYmd(endDate) && startDate <= endDate

  const prior = rangeValid ? priorRange(startDate, endDate, priorMode) : null
  const source = channel ? { order_source: channel } : {}
  const currentFilters = rangeValid ? { ...dateRangeToUtc(startDate, endDate), ...source } : null

  const { data, loading, error } = useQuery<{
    sold: { byHourProduct: HourProductRow[] }
    staff: { byHourProduct: HourProductRow[] }
    prior: { byHourProduct: HourProductRow[] }
    revenue: { rows: RevenueRow[] }
  }>(GET_PRODUCT_RECONCILIATION, {
    variables:
      rangeValid && prior
        ? {
            current: currentFilters,
            prior: { ...dateRangeToUtc(prior.start, prior.end), ...source },
          }
        : undefined,
    skip: !rangeValid,
    fetchPolicy: 'cache-and-network',
  })

  // The catalog join only adds zero-sales rows and badges — a failure here
  // (e.g. no catalog permission) must not take the quantities down with it.
  const { data: menuData, error: menuError } = useQuery<{ products: MenuProduct[] }>(GET_MENU_INDEX, {
    fetchPolicy: 'cache-first',
  })

  const { data: voidData } = useQuery<{ productVoidStats: { rows: VoidRow[] } }>(
    GET_PRODUCT_VOID_STATS,
    {
      variables: currentFilters ? { filters: currentFilters } : undefined,
      fetchPolicy: 'cache-and-network',
      skip: !VOIDS_API_READY || !rangeValid,
    }
  )

  const { rows, hasMoney, unattributedRevenueCents } = useMemo(() => {
    const map = new Map<string, ReconciliationRow>()

    const ensure = (productId: number, productName: string, variantName: string) => {
      const key = productVariantKey(productId, variantName)
      let row = map.get(key)
      if (!row) {
        row = {
          key,
          productId,
          productName,
          variantName,
          label: labelOf(productName, variantName),
          sold: 0,
          staff: 0,
          made: 0,
          prior: 0,
          cancelled: VOIDS_API_READY ? 0 : null,
          refunded: VOIDS_API_READY ? 0 : null,
          revenueCents: null,
          status: null,
        }
        map.set(key, row)
      }
      return row
    }

    // Quantities: byHourProduct summed over the hours is period-exact.
    for (const r of data?.sold?.byHourProduct ?? []) {
      ensure(r.productId, r.productName, r.variantName).sold += r.qtyScheduled
    }
    for (const r of data?.staff?.byHourProduct ?? []) {
      ensure(r.productId, r.productName, r.variantName).staff += r.qtyScheduled
    }
    for (const r of data?.prior?.byHourProduct ?? []) {
      ensure(r.productId, r.productName, r.variantName).prior += r.qtyScheduled
    }
    for (const row of map.values()) row.made = row.sold + row.staff

    // Revenue is "as sold" (ORDERED, carrier rule): it is attached to the rows
    // that exist, never used to create one. A webshop meal deal's euros sit on
    // the deal PARENT line, which PRODUCED explodes away — that revenue is
    // pooled into one visible "Meal deals" line so the total still ties to the
    // sales reports. (POS-built deals have no parent; their premium lands on
    // the main dish's row, which exists.)
    let moneyVisible = false
    let unattributed = 0
    for (const r of data?.revenue?.rows ?? []) {
      if (r.revenueExVatCents == null) continue
      moneyVisible = true
      const row = map.get(productVariantKey(r.productId, r.variantName))
      if (row) row.revenueCents = (row.revenueCents ?? 0) + r.revenueExVatCents
      else unattributed += r.revenueExVatCents
    }

    for (const r of voidData?.productVoidStats?.rows ?? []) {
      const row = ensure(r.productId, r.productName, r.variantName)
      row.cancelled = (row.cancelled ?? 0) + r.qtyCancelled
      row.refunded = (row.refunded ?? 0) + r.qtyRefunded
    }

    // Catalog join: badges for what sold, plus a zero row for everything on the
    // menu that did not. Bundles/deals are absent by construction (the document
    // asks for kind: BASE) — PRODUCED explodes them, so a deal row is noise.
    const products = menuData?.products ?? []
    if (products.length > 0) {
      const byId = new Map<number, MenuProduct>()
      for (const p of products) byId.set(p.id, p)

      for (const row of map.values()) {
        const product = byId.get(row.productId)
        const variant = product?.variants.find((v) => v.name === row.variantName)
        if (!product || !variant) row.status = 'not in menu'
        else if (!product.active || !variant.active) row.status = 'archived'
      }

      for (const product of products) {
        if (!product.active) continue
        for (const variant of product.variants) {
          if (!variant.active) continue
          const key = productVariantKey(product.id, variant.name)
          if (!map.has(key)) ensure(product.id, product.name, variant.name)
        }
      }
    }

    const list = [...map.values()].sort((a, b) => b.made - a.made || a.label.localeCompare(b.label))
    return { rows: list, hasMoney: moneyVisible, unattributedRevenueCents: unattributed }
  }, [data, menuData, voidData])

  const term = search.trim().toLowerCase()
  const visible = term ? rows.filter((r) => r.label.toLowerCase().includes(term)) : rows

  // The pooled deal-parent revenue is period-wide, so it only renders (and only
  // joins the total) when no search filter is narrowing the table.
  const showDealRow = hasMoney && unattributedRevenueCents > 0 && !term

  const totals = visible.reduce(
    (acc, r) => ({
      sold: acc.sold + r.sold,
      staff: acc.staff + r.staff,
      made: acc.made + r.made,
      prior: acc.prior + r.prior,
      cancelled: acc.cancelled + (r.cancelled ?? 0),
      refunded: acc.refunded + (r.refunded ?? 0),
      revenue: acc.revenue + (r.revenueCents ?? 0),
    }),
    { sold: 0, staff: 0, made: 0, prior: 0, cancelled: 0, refunded: 0, revenue: 0 }
  )

  const handleExport = () => {
    const header = [
      'Product',
      'Variant',
      'Status',
      'Sold',
      'Staff',
      'Made',
      'Cancelled',
      'Refunded',
      'Prev',
      'Delta',
      'Delta %',
    ]
    if (hasMoney) header.push('Revenue ex VAT')

    const csvRows = visible.map((r) => {
      const delta = r.made - r.prior
      const pct = r.prior > 0 ? ((delta / r.prior) * 100).toFixed(1).replace('.', ',') : null
      const row: (string | number | null)[] = [
        r.productName,
        r.variantName,
        r.status,
        r.sold,
        r.staff,
        r.made,
        r.cancelled,
        r.refunded,
        r.prior,
        delta,
        pct,
      ]
      if (hasMoney) row.push(csvEuros(r.revenueCents))
      return row
    })

    if (showDealRow) {
      csvRows.push([
        'Meal deals (revenue as sold)',
        '', null, null, null, null, null, null, null, null, null,
        csvEuros(unattributedRevenueCents),
      ])
    }

    downloadCsvFromRows(`product-reconciliation-${startDate}-${endDate}.csv`, header, csvRows)
  }

  // No early return on loading/error: the range controls must stay reachable,
  // or a failing range would lock the user out of changing it.
  return (
    <div className="space-y-4">
      <DateRangeBar
        startDate={startDate}
        endDate={endDate}
        onStartChange={onStartChange}
        onEndChange={onEndChange}
      />

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products…"
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 w-56 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />

        <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          {PRIOR_MODES.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setPriorMode(mode.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                priorMode === mode.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {prior && (
          <span className="text-xs text-gray-400">
            compared with {prior.start} – {prior.end}
          </span>
        )}

        <button
          onClick={handleExport}
          disabled={visible.length === 0}
          className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {menuError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
          The menu could not be loaded, so zero-sales products and the archived / not-in-menu badges
          are missing. The quantities below are unaffected.
        </div>
      )}

      {data && !hasMoney && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
          Revenue requires financial permission — the table and the CSV export show quantities only.
        </div>
      )}

      {!rangeValid ? (
        <p className="text-sm text-gray-400 px-1">Pick a complete From/To range to load the report.</p>
      ) : loading && !data ? (
        <TableLoading />
      ) : error ? (
        <QueryError message={`Failed to load product reconciliation: ${error.message}`} />
      ) : rows.length === 0 ? (
        <EmptyData />
      ) : visible.length === 0 ? (
        <p className="text-sm text-gray-400 px-1">No product matches "{search.trim()}".</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <TH>Product</TH>
                  <TH right>Sold</TH>
                  <TH right>Staff</TH>
                  <TH right>Made</TH>
                  <TH right>Cancelled</TH>
                  <TH right>Refunded</TH>
                  <TH right>Prev</TH>
                  <TH right>Δ</TH>
                  {hasMoney && <TH right>Revenue ex VAT</TH>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visible.map((r) => {
                  const delta = r.made - r.prior
                  const pct = r.prior > 0 ? (delta / r.prior) * 100 : null
                  const deltaTone =
                    delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-gray-400'
                  return (
                    <tr
                      key={r.key}
                      onClick={() =>
                        onOpenDrill({
                          productId: r.productId,
                          variantId: null,
                          productName: r.productName,
                          variantName: r.variantName,
                          start: startDate,
                          end: endDate,
                        })
                      }
                      className={`cursor-pointer transition-colors ${
                        r.made === 0 ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-2.5 text-gray-900">
                        <span className="font-medium">{r.productName}</span>
                        {r.variantName && r.variantName !== r.productName && (
                          <span className="text-gray-400"> · {r.variantName}</span>
                        )}
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{r.sold}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                        {r.staff > 0 ? r.staff : <span className="text-gray-300">0</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">
                        {r.made}
                      </td>
                      <td
                        className="px-4 py-2.5 text-right tabular-nums text-gray-700"
                        title={r.cancelled == null ? 'Waiting for API deploy' : undefined}
                      >
                        {r.cancelled ?? <span className="text-gray-300">—</span>}
                      </td>
                      <td
                        className="px-4 py-2.5 text-right tabular-nums text-gray-700"
                        title={r.refunded == null ? 'Waiting for API deploy' : undefined}
                      >
                        {r.refunded ?? <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{r.prior}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${deltaTone}`}>
                        {delta === 0 ? (
                          '—'
                        ) : (
                          <>
                            {delta > 0 ? `+${delta}` : delta}
                            {pct != null && (
                              <span className="text-xs text-gray-400 ml-1">
                                {pct > 0 ? '+' : ''}
                                {pct.toFixed(0)}%
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      {hasMoney && (
                        <td className="px-4 py-2.5 text-right text-gray-700">
                          {r.revenueCents != null ? formatEuros(r.revenueCents) : '—'}
                        </td>
                      )}
                    </tr>
                  )
                })}
                {showDealRow && (
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-2.5 text-gray-500 italic">
                      Meal deals (revenue as sold, exploded above as components)
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-300">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-300">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-300">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-300">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-300">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-300">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-300">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-700">
                      {formatEuros(unattributedRevenueCents)}
                    </td>
                  </tr>
                )}
                <tr className="border-t border-gray-100 font-medium bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-900">Total ({visible.length} products)</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">{totals.sold}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">{totals.staff}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">
                    {totals.made}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                    {VOIDS_API_READY ? totals.cancelled : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                    {VOIDS_API_READY ? totals.refunded : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{totals.prior}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                    {totals.made - totals.prior > 0 ? '+' : ''}
                    {totals.made - totals.prior}
                  </td>
                  {hasMoney && (
                    <td className="px-4 py-2.5 text-right text-gray-900">
                      {formatEuros(totals.revenue + (showDealRow ? unattributedRevenueCents : 0))}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && (
        <p className="text-xs text-gray-400">
          Quantities are produced units: meal deals are counted as their components. Made = Sold +
          Staff, and the comparison column is on the same Made basis. Revenue is "as sold" — a
          webshop deal's euros sit on the deal line, pooled into the "Meal deals" line above the
          total, so component rows can show units without revenue.
          {!VOIDS_API_READY && ' Cancelled and refunded quantities are waiting for an API deploy.'}
        </p>
      )}
    </div>
  )
}

export default ReconciliationTab
